import { useMount } from 'ahooks';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { Form, Radio, Select } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import {
  Caption,
  SectionHeading,
  SubHeading,
  TextHeavy,
} from 'components/RevxTypography';
import {
  PlatformOptionPaperScope,
  PlatformSkuPaperUpdateParams,
} from 'domains/jobconfigDomain';
import {
  PlatformSkuPaperOptions,
  SKU,
  SkuPaperOptionCombs,
  StepActionTypeEnum,
} from 'domains/skuDomain';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { StepComponentProps } from '.';
import './Step3Paper.less';
import { SearchTable } from './components/SearchTable';

const OPTION_COMB_LENGTH = 4;

const optionCombsDefaultValue: SkuPaperOptionCombs = {
  categoryOption: '',
  basisWeightOptions: [],
  finishOptions: [],
};

export const Step3Paper = ({ sku, topSku, skuPart }: StepComponentProps) => {
  const { t } = useTranslation();
  const { metric } = useMeasurementUnit();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const [paperOption, setPaperOption] = useState<PlatformSkuPaperOptions>();
  const [skipPaperOptions, setSkipPaperOptions] = useState(false);
  const [hidePaperSearchSection, setHidePaperSearchSection] = useState(false);
  const hidePaperSearchSectionRef = useRef(false);

  const updateHidePaperSearchSection = (value: boolean) => {
    hidePaperSearchSectionRef.current = value;
    setHidePaperSearchSection(value);
  };
  const [optionPaperList, setOptionPaperList] =
    useState<PlatformOptionPaperScope>({
      allPapers: [],
      myPapers: [],
    });

  const formItemLayout = {
    labelCol: { span: 10 },
    wrapperCol: { span: 6 },
  };

  useMount(async () => {
    dispatch.sku.setRevxSpinloading(true);
    try {
      dispatch.sku.registerStepAction({
        [StepActionTypeEnum.STEP3_PAPER]: autoSubmit,
      });
      const options = await dispatch.sku.getPaperOptions();
      setPaperOption(options);
      const { skuPaperOptions } = sku;
      let combOptions = [],
        curCombOptionsLen = 0;
      if (skuPaperOptions?.optionCombs.length) {
        combOptions.push(...skuPaperOptions.optionCombs);
        curCombOptionsLen =
          OPTION_COMB_LENGTH - skuPaperOptions.optionCombs.length;
      } else {
        curCombOptionsLen = OPTION_COMB_LENGTH;
      }
      for (let i = 0; i < curCombOptionsLen; i++) {
        combOptions.push(optionCombsDefaultValue);
      }
      const skip = Boolean(sku.skipPaperOptions);
      setSkipPaperOptions(skip);
      updateHidePaperSearchSection(
        skip ? true : Boolean(sku.hidePaperSearchSection)
      );
      form.setFieldsValue({
        skipPaperOptions: skip,
        hidePaperConstraintSection: skip
          ? true
          : Boolean(sku.hidePaperConstraintSection),
        colorOptions: skuPaperOptions?.colorOptions || [],
        constraint1Options: skuPaperOptions?.constraint1Options,
        constraint2Options: skuPaperOptions?.constraint2Options,
        optionCombs: combOptions,
      });
      await getPaperList(sku);
    } finally {
      dispatch.sku.setRevxSpinloading(false);
    }
  });

  const organizeStructure = (arr: string[]) => {
    return arr.map((item) => {
      return { label: item, value: item, key: item };
    });
  };

  /**
   * Basis weight options, labelled in the unit being viewed.
   *
   * The value stays the supplier's own string - the paper pickers match selections on it - and
   * only the label changes, so the selected tags read in gsm without anything being restated.
   * A basis weight has no single metric value, so the API keys its labels by grade as well.
   */
  const basisWeightOptions = (arr: string[], category?: string) => {
    return arr.map((item) => {
      const shown =
        (category && paperOption?.basisWeightsMetric?.[`${category}|${item}`]) ||
        item;
      return { label: metric ? shown : item, value: item, key: item };
    });
  };

  const onValuesChange = async (changedValues: {
    skipPaperOptions?: boolean;
  }) => {
    if (changedValues.skipPaperOptions !== undefined) {
      const skip = Boolean(changedValues.skipPaperOptions);
      setSkipPaperOptions(skip);
      if (skip) {
        form.setFieldsValue({
          hidePaperConstraintSection: true,
        });
        updateHidePaperSearchSection(true);
      }
    }
  };

  const autoSubmit = async (sku: Partial<SKU>) => {
    if (sku.readonly) {
      return true;
    }
    return await updatePaperOptions(sku, false);
  };

  const updatePaperOptions = async (
    sku: Partial<SKU>,
    manualSave?: boolean
  ) => {
    if (!sku?.id || sku.version === undefined) {
      console.warn('SKU id or version is missing', sku);
      return true;
    }
    dispatch.sku.setRevxSpinloading(true);
    const params: PlatformSkuPaperUpdateParams = {
      id: sku.id,
      skuPaperOptions: form.getFieldsValue(),
      skipPaperOptions: form.getFieldsValue().skipPaperOptions,
      hidePaperConstraintSection:
        form.getFieldsValue().hidePaperConstraintSection,
      hidePaperSearchSection: hidePaperSearchSectionRef.current,
      paperEquivalentCode: sku.paperEquivalentCode || '',
      paperSearchScope: sku.paperSearchScope,
      version: sku.version,
    };
    await dispatch.sku.platformUpdatePaperHelper(params);
    if (!manualSave) return true;
    await getPaperList(sku);
    dispatch.sku.setRevxSpinloading(false);
    return true;
  };

  const getPaperList = async (sku: Partial<SKU>) => {
    const res = await skuService.getPlatformOptionPaper(sku.id || 0);
    setOptionPaperList(res);
  };

  return (
    <div className="platform-step3-paper">
      <div className="single-sku-config-wizard-step-title">
        {t('platform.sku.wizard.define')}{' '}
        <span className="brand-green">
          {t('platform.sku.wizard.papersTypes')}
        </span>
      </div>
      <div className="single-sku-config-wizard-step-content">
        <SectionHeading>{t('platform.sku.wizard.paper.title')}</SectionHeading>
        <SubHeading>{t('platform.sku.wizard.paper.subTitle')}.</SubHeading>
        <p>
          <TextHeavy style={{ paddingRight: 10 }}>
            {t('platform.sku.wizard.paper.getInspired')}!
          </TextHeavy>

          <Caption>{t('platform.sku.wizard.paper.tip')}:</Caption>
        </p>
        <Form
          layout={'horizontal'}
          form={form}
          {...formItemLayout}
          autoComplete="off"
          onValuesChange={onValuesChange}
        >
          <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={t('platform.sku.wizard.quantity.form.label.paperSkip')}
            name="skipPaperOptions"
            className="skip-options"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={t(
              'platform.sku.wizard.quantity.form.label.hidePaperConstraintSection'
            )}
            name="hidePaperConstraintSection"
            className="skip-options"
          >
            <Radio.Group
              disabled={sku.readonly || Boolean(topSku) || skipPaperOptions}
            >
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <RevxFlexLayout className="form-list-wrap">
            <Form.Item
              label={t('platform.sku.wizard.paper.form.label.color')}
              name="colorOptions"
            >
              <Select
                disabled={sku.readonly}
                mode="multiple"
                style={{ width: 300 }}
                placeholder="Please select"
                options={organizeStructure(paperOption?.colors || [])}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              />
            </Form.Item>

            <Form.Item
              label={t('platform.mat.form.itemLabels.constraint1')}
              name="constraint1Options"
            >
              <Select
                disabled={sku.readonly}
                mode="multiple"
                style={{ width: 300 }}
                placeholder="Please select"
                options={organizeStructure(
                  paperOption?.constraint1Options || []
                )}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('platform.mat.form.itemLabels.constraint2')}
              name="constraint2Options"
            >
              <Select
                disabled={sku.readonly}
                mode="multiple"
                style={{ width: 300 }}
                placeholder="Please select"
                options={organizeStructure(
                  paperOption?.constraint2Options || []
                )}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              />
            </Form.Item>
          </RevxFlexLayout>

          <RevxFlexLayout className="form-list-wrap">
            <Form.List name="optionCombs">
              {(fields, operation, meta) => {
                return fields.map((field) => {
                  return (
                    <div className="form-list">
                      <div className="form-list__title">
                        Paper combination {field.key + 1}
                      </div>
                      <Form.Item
                        label={t(
                          'platform.sku.wizard.paper.form.label.category'
                        )}
                        name={[field.name, 'categoryOption']}
                      >
                        <Select
                          disabled={sku.readonly}
                          showSearch
                          style={{ width: 300 }}
                          allowClear={true}
                          placeholder="Please select"
                          options={organizeStructure(
                            paperOption?.categories || []
                          )}
                          getPopupContainer={(e) => {
                            return e as HTMLElement;
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label={t(
                          'platform.sku.wizard.paper.form.label.basisWeight'
                        )}
                        name={[field.name, 'basisWeightOptions']}
                      >
                        <Select
                          disabled={sku.readonly}
                          mode="multiple"
                          style={{ width: 300 }}
                          placeholder="Please select"
                          options={basisWeightOptions(
                            paperOption?.basisWeights || [],
                            form.getFieldValue([
                              'optionCombs',
                              field.name,
                              'categoryOption',
                            ])
                          )}
                          getPopupContainer={(e) => {
                            return e as HTMLElement;
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label={t('platform.sku.wizard.paper.form.label.finish')}
                        name={[field.name, 'finishOptions']}
                      >
                        <Select
                          disabled={sku.readonly}
                          mode="multiple"
                          style={{ width: 300 }}
                          placeholder="Please select"
                          options={organizeStructure(
                            paperOption?.finishes || []
                          )}
                          getPopupContainer={(e) => {
                            return e as HTMLElement;
                          }}
                        />
                      </Form.Item>
                    </div>
                  );
                });
              }}
            </Form.List>
          </RevxFlexLayout>
        </Form>
        <RevxFlexLayout className="filter-btn" justify="justify-end">
          <RevxButton
            brandGreen
            onClick={async () => {
              await updatePaperOptions(sku, true);
            }}
          >
            {t('common.save')}
          </RevxButton>
        </RevxFlexLayout>
        <RevxFlexLayout className="hide-search-section" align="center">
          <span className="hide-search-section__label">
            {t('platform.sku.wizard.quantity.form.label.hidePaperSearchSection')}
            :
          </span>
          <Radio.Group
            value={hidePaperSearchSection}
            disabled={sku.readonly || Boolean(topSku) || skipPaperOptions}
            onChange={(e) => updateHidePaperSearchSection(e.target.value)}
          >
            <Radio value={true}>
              <span>Yes</span>
            </Radio>
            <Radio value={false}>
              <span>No</span>
            </Radio>
          </Radio.Group>
        </RevxFlexLayout>
        <div id="search-table">
          <SearchTable
            newOptionPapers={optionPaperList}
            sku={sku}
            onChange={async (curSku) => {
              console.log('SearchTable onChange', curSku);
              const curItem = { ...sku, ...curSku };
              console.log('curItem', curItem);
              await updatePaperOptions(curItem);
              dispatch.sku.setRevxSpinloading(false);
            }}
          />
        </div>
      </div>
    </div>
  );
};
